import { useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { defineMessages, useIntl, type IntlShape } from "react-intl";

import type { Brand } from "@aglocal/brands";
import { getBrandFromEmail } from "@aglocal/brands";
import type { Interview } from "@aglocal/schema/Interview";
import {
  PostMeetingForm,
  type PostMeetingSummary,
} from "@aglocal/schema/PostMeetingForm";
import {
  SFDC_LONG_TEXT_LIMIT,
  measureSkillsPayload,
} from "@aglocal/schema/PostMeetingLimits";

import UniqueList from "@aglocal/web/helpers/UniqueList";
import normalizeSkill from "@aglocal/web/helpers/normalizeSkill";
import { effectTsResolver } from "@hookform/resolvers/effect-ts";
import { Schema } from "effect";
import { useMissionControl } from "@aglocal/web/mission-control";
import { InterviewMetricsNotesResponse, ACTIVITY_TYPES } from "@aglocal/types";

import getJobTitle from "@/helpers/getJobTitle";

import { FIELD_LABELS } from "./messages";

export const PostMeetingFormWithSummary = PostMeetingForm;

export type PostMeetingFormWithSummary = typeof PostMeetingFormWithSummary.Type;

export const VALIDATION_MESSAGES = defineMessages({
  jobTitleRequired: {
    defaultMessage: "Please enter a job title",
    description: "Validation error shown when the job title field is empty.",
  },
  summaryOrNotesRequired: {
    defaultMessage: "Please enter a meeting summary or notes",
    description:
      "Validation error shown on the notes and summary fields when both are empty.",
  },
  textLimit: {
    defaultMessage:
      "{field} must not exceed {limit, number} characters (currently {count, number})",
    description:
      "Validation error for a text field over the Salesforce length limit; {field} is the field label, {limit} the maximum and {count} the current length.",
  },
  skillsLimit: {
    defaultMessage:
      "The Skills list exceeds the {limit, number} character Salesforce limit by {excess, number} characters. Remove some skills.",
    description:
      "Validation error when the skills list is over the Salesforce length limit; {limit} is the maximum and {excess} how many characters over it is.",
  },
});

/** Long text fields whose length Salesforce limits, keyed by form field name. */
const LIMITED_TEXT_FIELDS = [
  "skillsComments",
  "goalsAndInterests",
  "locationComments",
] as const;

/**
 * Builds the wrap-up form schema with validation messages in the user's
 * language. The job title rule from the shared schema is re-declared here
 * because its message is fixed in English.
 */
export function createPostMeetingFormSchema(intl: IntlShape) {
  const { formatMessage } = intl;

  return Schema.extend(
    PostMeetingForm.omit("jobTitle"),
    Schema.Struct({
      jobTitle: Schema.String.pipe(
        Schema.nonEmptyString({
          message: () => formatMessage(VALIDATION_MESSAGES.jobTitleRequired),
        }),
      ),
    }),
  ).pipe(
    Schema.filter((data) => {
      const issues: Schema.FilterIssue[] = [];

      if (data.notes.trim() === "" && data.summary.value.trim() === "") {
        const message = formatMessage(
          VALIDATION_MESSAGES.summaryOrNotesRequired,
        );
        issues.push(
          { path: ["notes"], message },
          { path: ["summary", "value"], message },
        );
      }

      for (const field of LIMITED_TEXT_FIELDS) {
        const count = data[field].length;
        if (count > SFDC_LONG_TEXT_LIMIT) {
          issues.push({
            path: [field],
            message: formatMessage(VALIDATION_MESSAGES.textLimit, {
              field: formatMessage(FIELD_LABELS[field]),
              limit: SFDC_LONG_TEXT_LIMIT,
              count,
            }),
          });
        }
      }

      // Salesforce stores skills as JSON, so the limit applies to the serialized payload
      const skillsPayload = measureSkillsPayload(
        Array.from(toUniqueSkillList(data.skills)),
      );
      if (skillsPayload > SFDC_LONG_TEXT_LIMIT) {
        issues.push({
          path: ["skills"],
          message: formatMessage(VALIDATION_MESSAGES.skillsLimit, {
            limit: SFDC_LONG_TEXT_LIMIT,
            excess: skillsPayload - SFDC_LONG_TEXT_LIMIT,
          }),
        });
      }

      return issues;
    }),
  );
}

function getDefaultMeetingType(
  candidateStatus: string | undefined,
  brand: Brand | null,
): (typeof ACTIVITY_TYPES)[number] {
  if (brand?.defaultActivityType) {
    return brand.defaultActivityType;
  }
  if (candidateStatus === "Current") {
    return "STP";
  }
  return "G2";
}

function useDefaultValues(
  interview: Interview,
  interviewMetrics?: InterviewMetricsNotesResponse,
): PostMeetingFormWithSummary {
  const { id, interviewee, interviewer, inMeetingNotes = "" } = interview;
  const { connectedDetails } = interviewee ?? {};
  const { candidateStatus } = connectedDetails ?? {};
  const currentJobTitle = getJobTitle(interview);
  const currentCompany = connectedDetails?.currentCompany ?? "";

  // Try reading from interviewee first (direct level)
  const { skillsComments, goalsAndInterests, locationComments } =
    connectedDetails ?? {};

  const brand = getBrandFromEmail(interviewer?.uId ?? "");

  return useMemo(() => {
    // Type the activity type more specifically and add validation
    const existingActivityType = interviewMetrics?.activityType as
      | (typeof ACTIVITY_TYPES)[number]
      | undefined;

    // Validate that the activity type is one of the allowed values
    const isValidActivityType = (
      type: string,
    ): type is (typeof ACTIVITY_TYPES)[number] =>
      ACTIVITY_TYPES.includes(type as (typeof ACTIVITY_TYPES)[number]);

    // Determine meeting type with proper type safety
    const defaultMeetingType = getDefaultMeetingType(candidateStatus, brand);
    const meetingType =
      existingActivityType && isValidActivityType(existingActivityType)
        ? existingActivityType
        : defaultMeetingType;

    return {
      id,
      meetingType: meetingType,
      jobTitle: currentJobTitle ?? "",
      currentCompany: currentCompany,
      notes: interviewMetrics?.recruiterNotes ?? inMeetingNotes,
      summary: { value: interviewMetrics?.summary ?? "" },
      skills: [],
      skillsComments: skillsComments ?? "",
      goalsAndInterests: goalsAndInterests ?? "",
      negativeSkills: [],
      locationComments: locationComments ?? "",
      additionalDetails: {}, // Use the default object here
    };
  }, [
    id,
    candidateStatus,
    brand,
    inMeetingNotes,
    currentJobTitle,
    currentCompany,
    skillsComments,
    goalsAndInterests,
    locationComments,
    interviewMetrics?.activityType,
    interviewMetrics?.recruiterNotes,
    interviewMetrics?.summary,
  ]);
}

export function usePostMeetingFormWithSummary(interview: Interview) {
  const intl = useIntl();
  const api = useMissionControl();
  const [interviewMetrics, setInterviewMetrics] = useState<
    InterviewMetricsNotesResponse | undefined
  >();
  const [isLoadingMetrics, setIsLoadingMetrics] = useState(true);

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setIsLoadingMetrics(true);
        const response = await api.getConnectedNoteDetails(interview.id);
        setInterviewMetrics(response);
      } catch (error) {
        console.error("Error fetching connected note details:", error);
        setInterviewMetrics(undefined);
      } finally {
        setIsLoadingMetrics(false);
      }
    };

    void fetchMetrics();
  }, [api, interview.id]);

  const defaultValues = useDefaultValues(interview, interviewMetrics);

  // Rebuilt when the language changes so validation messages follow it
  const resolver = useMemo(
    () => effectTsResolver(createPostMeetingFormSchema(intl)),
    [intl],
  );

  const form = useForm({
    defaultValues,
    resolver,
  });

  const idRef = useRef(interview.id);
  const { reset } = form;

  useEffect(() => {
    // Metrics arrive async; a full reset must not wipe the skills that
    // SkillsField already synced from the existing and suggested skills
    reset((currentValues) => ({
      ...defaultValues,
      skills:
        idRef.current === interview.id
          ? currentValues.skills
          : defaultValues.skills,
    }));
    idRef.current = interview.id;
  }, [defaultValues, reset, interview.id]);

  return {
    ...form,
    isMeetingTypeDisabled: interviewMetrics?.activityType != null,
    isLoadingMetrics,
    disableSave: interviewMetrics?.editable === false,
  };
}

function toUniqueSkillList(skills?: readonly string[]): UniqueList<string> {
  const list = new UniqueList<string>([], normalizeSkill);
  if (!skills) {
    return list;
  }

  skills.forEach((skill) => {
    if (typeof skill !== "string") {
      return;
    }
    const cleaned = skill.trim().replace(/\s+/g, " ");
    if (cleaned.length > 0) {
      list.add(cleaned);
    }
  });

  return list;
}

/**
 * Prepares a generated summary for the form. When generation produced no text
 * (for example with an empty transcript) the model settings are dropped, so a
 * summary the recruiter then writes by hand is not attributed to that model.
 * The name is kept so edits stay cached per model.
 */
export function toFormSummary(summary: PostMeetingSummary): PostMeetingSummary {
  if (summary.value.trim() !== "") {
    return summary;
  }
  return { name: summary.name, value: summary.value };
}

export function getConnectedDetailsUpdate(
  { notes, summary, ...data }: PostMeetingFormWithSummary,
  existingSkills: readonly string[] = [],
): PostMeetingForm {
  const updatedSkills = toUniqueSkillList(data.skills);
  const priorSkills = toUniqueSkillList(existingSkills);
  const removedSkills = UniqueList.difference(priorSkills, updatedSkills);

  return {
    ...data,
    skills: Array.from(updatedSkills),
    negativeSkills: Array.from(removedSkills),
    notes: notes,
    // name and settings travel with the summary so the server can record which model produced it
    summary,
  };
}
